import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetch2, fetch3, fetch4, fetch5 } from "../commonApis/commonApis";
const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
}

// -------------------- Zone Apis Starts's Here -------------------- //

export const useZoneCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {

      const res: ApiResponse = await fetch4(
        `${url}/api/zoneManagement/`,
        payload
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Zone  Created successfully:", data);
      // Invalidate zone-related queries
      queryClient.invalidateQueries({ queryKey: ["zoneManagementData"] });
    },
    onError: (error: any) => {
      console.error("❌ Zone create request failed:", error.message);
    },
  });
};

export const useFetchZoneData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["zoneManagementData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/zoneManagement/?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

interface UpdateZonePayload {
  id: string | any;
  name: string | any;
  tireId: string | any;
  courierCompanyId: string | any;

  paymentMode: string | any;
  capacityPerDay: string | any;
  pricePerShipment?: string | any;
}
export const useUpdateZoneData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      tireId,
      courierCompanyId,

      paymentMode,
      capacityPerDay,
      pricePerShipment,
    }: UpdateZonePayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/zoneManagement/${id}`,
        "PATCH",
        {
          name,
          tireId,
          courierCompanyId,

          paymentMode,
          capacityPerDay,
          pricePerShipment,
        }
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Zone update successfully:", data);
      // Invalidate zone-related queries
      queryClient.invalidateQueries({ queryKey: ["zoneManagementData"] });
    },
    onError: (error: any) => {
      console.error("❌ Zone update request failed:", error.message);
    },
  });
};

interface UpdateZoneStatusPayload {
  id: string;
  status: any; // Add more statuses if needed
}
export const useUpdateZoneStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: UpdateZoneStatusPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/zoneManagement/${id}`,
        "PATCH",
        {
          status,
        }
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Zone status updated successfully:", data);
      // Invalidate zone-related queries
      queryClient.invalidateQueries({ queryKey: ["zoneManagementData"] });
    },
    onError: (error: any) => {
      console.error("❌ Zone status update failed:", error.message);
    },
  });
};

// -------------------- Zone Apis End's Here -------------------- //

// -------------------- Tire Apis Starts's Here -------------------- //

export const useTireCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {

      const res: ApiResponse = await fetch4(`${url}/api/tire/`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Tire  Created successfully:", data);
      // Invalidate all tire-related queries to refresh the dropdown
      queryClient.invalidateQueries({ queryKey: ["tireData"] });
    },
    onError: (error: any) => {
      console.error("❌ Tire create request failed:", error.message);
    },
  });
};

export const useFetchTireData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["tireData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/tire/?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM`,
        "get"
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};
export const useFetchAllTireData = () => {
  return useQuery({
    queryKey: ["tireData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/tire/all`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data ?? []; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};
interface UpdateTirePayload {
  id: string;
  name: string;
  countryId: string | null;
  warehouseId: string;
  coverageCity: string | null;
  status?: any;
}
export const useUpdateTireData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      countryId,
      warehouseId,
      coverageCity,
      status,
    }: UpdateTirePayload) => {
      const res: ApiResponse = await fetch5(`${url}/api/tire/${id}`, "PATCH", {
        name,
        countryId,
        warehouseId,
        coverageCity,
        status,
      });

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Tire update successfully:", data);
      // Invalidate all tire-related queries
      queryClient.invalidateQueries({ queryKey: ["tireData"] });
    },
    onError: (error: any) => {
      console.error("❌ Tire update request failed:", error.message);
    },
  });
};

interface UpdateTireStatusPayload {
  id: string;
  status: any; // Add more statuses if needed
}
export const useUpdateTireStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: UpdateTireStatusPayload) => {
      const res: ApiResponse = await fetch5(`${url}/api/tire/${id}`, "PATCH", {
        status,
      });

      if (!res.success) {
        throw new Error(res.message || "Failed to update vehicle status");
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Tire status updated successfully:", data);
      // Invalidate all tire-related queries
      queryClient.invalidateQueries({ queryKey: ["tireData"] });
    },
    onError: (error: any) => {
      console.error("❌ Tire status update failed:", error.message);
    },
  });
};

// -------------------- Tire Apis Ends Here -------------------- //

export const useFetchAllShippingPartnersData = () => {
  return useQuery({
    queryKey: ["shippingPartnersData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/shippingPartners/all`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data ?? []; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};
